import { useState, useEffect, useCallback } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useToast } from '@/components/ui/use-toast';
import { getTestDataSummary, cleanupTestData } from '@/services/tenantService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Contextual banner that appears only when the current tenant has seeded test
 * data (TEN-2.5). Lets a tenant admin remove it in one action. Renders nothing
 * for non-admins, or when there's no test data. Used on the Pipeline board and
 * the Settings page.
 */
export function TestDataBanner({ className = '' }: { className?: string }) {
  const { currentTenant } = useTenant();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const canManage = hasPermission('manage_settings') || hasPermission('system.override');
  const tenantId = currentTenant?.tenant_id;

  const [counts, setCounts] = useState<{ projects: number; users: number } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const refresh = useCallback(async () => {
    if (!canManage || !tenantId) {
      setCounts(null);
      return;
    }
    setCounts(await getTestDataSummary(tenantId));
  }, [canManage, tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!canManage || !tenantId || !counts || counts.projects <= 0) {
    return null;
  }

  const handleRemove = async () => {
    setIsRemoving(true);
    const res = await cleanupTestData(tenantId);
    setIsRemoving(false);
    setConfirmOpen(false);
    if (res.success) {
      toast({
        title: 'Test data removed',
        description: `Removed ${res.projects_deleted ?? 0} project(s) and ${res.users_deleted ?? 0} synthetic user(s). Your configuration was not affected.`,
      });
      refresh();
    } else {
      toast({
        title: 'Error',
        description: res.error || 'Failed to remove test data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 ${className}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FlaskConical className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">
          This tenant contains <strong>demo / test data</strong> — {counts.projects} test
          project{counts.projects === 1 ? '' : 's'}
          {counts.users > 0
            ? ` and ${counts.users} synthetic user${counts.users === 1 ? '' : 's'}`
            : ''}
          .
        </span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-shrink-0 border-amber-400 hover:bg-amber-100"
        onClick={() => setConfirmOpen(true)}
        disabled={isRemoving}
      >
        {isRemoving ? (
          <>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            Removing…
          </>
        ) : (
          'Remove test data'
        )}
      </Button>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (!isRemoving) setConfirmOpen(o);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove test data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the {counts.projects} seeded test project
              {counts.projects === 1 ? '' : 's'} (and their checklist items, followers, and
              documents) and the {counts.users} synthetic test user
              {counts.users === 1 ? '' : 's'} in this tenant. Your real configuration —
              pipelines, checklists, notification templates, theme, and dashboards — is not
              affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRemove();
              }}
              disabled={isRemoving}
            >
              {isRemoving ? 'Removing…' : 'Remove test data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
